import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import sessionStorage from "./utils/sessionStore";
import { Form, Button, Alert, Spinner, Badge } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import SearchableSelect from "./components/SearchableSelect";
import CollapsibleFilters from "./components/CollapsibleFilters";
import GridPagination from "./components/GridPagination";
import { authHeaders } from "./QuoteCreate";
import { isAdminOrDistrRole } from "./utils/auth";
import { buildCustomerConfig } from "./utils/refDataConfigs";
import * as searchCache from "./utils/searchCache";
import { SHIPMENTS_KEY, normalizeShipmentsResponse } from "./utils/searchCache";
import "./pagecss/Webappcss.css";
import "./pagecss/DynamicQw.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// -----------------------------------------------------------------------------
// Role-aware autocomplete scope — identical to Order.js / Quote.js. Territory /
// Sales Rep / Customer dropdowns narrow one another client-side; territory is
// ALSO sent to the API as distributorPrefix (the shipments endpoint matches it
// against SalesRepList, e.g. "D57" catches every D57-* rep's shipments).
// -----------------------------------------------------------------------------
function getCurrentUserScope() {
  const role = (sessionStorage.getItem("userRole") || "").toLowerCase();
  const territoryId = sessionStorage.getItem("territoryId") || "";
  const isD99 = territoryId.toLowerCase() === "d99";
  return { role, territoryId, isUnrestricted: isAdminOrDistrRole(role) && isD99 };
}

const PAGE_SIZES = [10, 25, 50, 100];

// -----------------------------------------------------------------------------
// Build the GET /api/shipments querystring for ONE page request. Pure — shared
// by runSearch and the background next-page prefetch so both produce identical
// requests.
// -----------------------------------------------------------------------------
// Exported: CustomerDetail's Activity tab reuses this for its shipment download
// so both build byte-identical /api/shipments requests.
export function buildShipmentSearchQs({ filters, page, pageSize, cursor, packNumFind }) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));
  // Cursor paging (live mode). The cached path ignores these and uses page.
  if (cursor?.dir === "next")      qs.set("beforePackNum", String(cursor.packNum));
  else if (cursor?.dir === "prev") qs.set("afterPackNum", String(cursor.packNum));
  const pnum = packNumFind.trim();
  if (/^\d+$/.test(pnum)) qs.set("packNum", pnum);          // exact pack match
  const onum = filters.orderNum.trim();
  if (/^\d+$/.test(onum)) qs.set("orderNum", onum);          // originating order
  if (filters.custID)       qs.set("custId", filters.custID);
  if (filters.trackingNumber.trim()) qs.set("trackingNumber", filters.trackingNumber.trim());
  if (filters.status) {
    qs.set("status", filters.status);
    // Voided rows are excluded by default — asking for them must include them.
    if (filters.status === "voided") qs.set("includeVoided", "true");
  }
  if (filters.invoiced === "yes")     qs.set("invoicedOnly", "true");
  else if (filters.invoiced === "no") qs.set("invoicedOnly", "false");
  // No startDate/endDate — the server applies its default window.
  qs.set("sortBy", "PackNum");
  qs.set("sortDir", "desc");
  return qs.toString();
}

// Status filter — single-select, mapped to ShipmentQueryParams:
//   shipped → status=shipped                (ShipStatus contains "shipped")
//   staged  → status=staged                 (ShipStatus contains "staged")
//   voided  → status=voided&includeVoided=true  (voided rows are excluded by
//             default server-side, so asking for them must also include them)
const STATUS_OPTIONS = [
  { label: "Any status", value: "" },
  { label: "Shipped",    value: "shipped" },
  { label: "Staged",     value: "staged" },
  { label: "Voided",     value: "voided" },
];

// Invoiced filter → invoicedOnly=true|false (omitted for "any").
const INVOICED_OPTIONS = [
  { label: "Any invoicing", value: "" },
  { label: "Invoiced",      value: "yes" },
  { label: "Not invoiced",  value: "no" },
];

// Table header / column-filter cell styles — same as the Order/Quote grids.
const QTH = { padding: "9px 12px", fontWeight: 700, whiteSpace: "nowrap" };
const QTHF = { padding: "3px 8px", borderTop: "1px solid #E2E8F0", verticalAlign: "middle", background: "#F8FAFC" };

function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}
// Issue 13 (OBS-03): Epicor stores no value on a pack header, so the API prices
// each pack from its lines in the background and answers the list inside a
// short budget. A row it has not priced yet comes back with amountPending=true
// and docOrderAmt=0 — that 0 is "unknown", not "$0.00". Show it as pending; the
// page re-reads itself until every amount is final (see REPRICE_DELAYS_MS).
function AmountCell({ s }) {
  if (s?.amountPending) {
    return (
      <span
        className="text-muted"
        title="Not priced yet — the pack value is computed from its order lines in the background; this page re-reads automatically."
      >
        pricing…
      </span>
    );
  }
  return fmtMoney(s?.docOrderAmt);
}

// Re-read schedule while a page still has pending amounts: a pack costs the API
// roughly one Epicor read plus one order read, four at a time, so a fresh 25-row
// page settles in ~10 s. Five tries, then stop (a later visit re-reads anyway).
const REPRICE_DELAYS_MS = [3000, 5000, 10000, 15000, 15000];

function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.valueOf())) return "—";
    return d.toLocaleDateString();
  } catch { return "—"; }
}
function fmtDateShort(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.valueOf())) return "—";
    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
  } catch { return "—"; }
}

// FedEx-ish carrier check — decides whether the tracking number renders as a
// deep link to FedEx tracking or as plain text (UPS/other carriers).
function isFedEx(s) {
  const v = (s?.shipViaCode || "") + " " + (s?.shipViaCodeDescription || "");
  return /fedex|fdx|fed ex/i.test(v);
}

// =============================================================================
// Shipment — shipping-history landing page. Mirrors the Orders page (Order.js):
// a scoped, paginated search grid over GET /api/shipments with a row-click →
// /order/:n (the order view shows the pack among its linked shipments). The
// /shipments route is wired in index.js; the DB menu row (RoutePath=/shipments)
// is added by SQL/025_AddShipmentsMenuItem.sql.
//
// Unlike orders there is no cache-warming poll; the only background re-read is
// the Issue 13 "pricing…" schedule (amounts the API has not priced yet).
// (Wire-casing note: every ShipmentListItemDTO field this page reads serializes
// to plain camelCase — no leading-acronym properties like PONum → pONum here.)
// =============================================================================
function Shipment() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-applied filters handed in via router state — e.g. the Customer page's
  // Activity tab navigates here with { initialFilters: { custID: ... } }.
  // Captured once in a ref so re-renders don't re-read stale router state.
  const initialFiltersRef = useRef(location.state?.initialFilters || null);
  const initialFilters = initialFiltersRef.current;

  // Auth gate — same pattern as Order / Quote.
  useEffect(() => {
    if (!sessionStorage.getItem("authToken")) navigate("/login");
  }, [navigate]);

  const userScope = useMemo(() => getCurrentUserScope(), []);

  // ── Filter state ────────────────────────────────────────────────────────
  // No Sales Rep / Territory filters: shipments carry no SalesRepList (the
  // "SalesRepList gap"), so the API silently ignores those params — offering
  // them here would be a lie. Customer/order/tracking/status cover the needs.
  const [filters, setFilters] = useState(() => ({
    custID: "", status: "", invoiced: "", trackingNumber: "", orderNum: "",
    // Router-state pre-fill wins over the blank defaults (Activity tab flow).
    ...(initialFilters || {}),
  }));

  const customerConfig = useMemo(
    () => buildCustomerConfig("", "", userScope),
    [userScope]
  );

  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Live mode pages by cursor: { dir: "next"|"prev", packNum }. null = first
  // page. Cleared whenever the result set changes (new search, page size).
  const [cursor, setCursor]     = useState(null);

  // ── Result state ──────────────────────────────────────────────────────────
  // Seed from the login-time prefetch when it's there (the default search is
  // cached at sign-in and after every default-filter search), so the grid
  // paints rows on first render; the mount effect then revalidates quietly.
  // With pre-applied filters the cached (unfiltered) default page would flash
  // wrong rows — start empty and let the mount search paint the filtered set.
  const [results, setResults]   = useState(
    () => (initialFilters
      ? { shipments: [], totalCount: null, totalPages: 0 }
      : searchCache.peek(SHIPMENTS_KEY) || { shipments: [], totalCount: null, totalPages: 0 })
  );
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  // A cache-seeded grid already IS a search result — render count + pager
  // immediately instead of the "hit Search" empty state.
  const [hasSearched, setHasSearched] = useState(() => !initialFilters && searchCache.peek(SHIPMENTS_KEY) !== undefined);
  // Pack # — exact-match server filter, finds across all pages.
  const [packNumFind, setPackNumFind] = useState("");
  // In-table client-side column filters (narrow the loaded page only).
  const emptyColFilters = { packNum: "", customer: "", orderNum: "", shipDate: "", shipVia: "", tracking: "", total: "", status: "" };
  const [colFilters, setColFilters] = useState(emptyColFilters);
  const setCol = (k, v) => setColFilters((c) => ({ ...c, [k]: v }));

  const [showColFilters, setShowColFilters] = useState(true);
  // Start collapsed on every screen size — the page auto-loads the newest
  // shipments anyway, so the grid gets the height; one tap on "Filters"
  // expands the panel when needed. Starts expanded when filters arrived via
  // router state, so the user sees which filters are pre-applied.
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => !initialFilters);
  const resultsScrollRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const refreshScrollCue = () => {
    const el = resultsScrollRef.current;
    if (!el) { setMoreBelow(false); return; }
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  };
  const onResultsScroll = (e) => {
    if (e.currentTarget.scrollTop > 24 && !filtersCollapsed) setFiltersCollapsed(true);
    refreshScrollCue();
  };
  useEffect(() => {
    const id = window.setTimeout(refreshScrollCue, 0);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, filtersCollapsed, loading]);

  // ───────────────────────────────────────────────────────────────────────────
  // runSearch — GET /api/shipments with the active filters. Envelope:
  // { success, shipments, totalCount, totalPages, page, pageSize, ... }.
  // ───────────────────────────────────────────────────────────────────────────
  // Per-result-set page cache: pageNum → normalized results, or a Promise for
  // a background prefetch still in flight. Visited pages rebind instantly on
  // Prev/Next, and the page AFTER the one on screen is always prefetching.
  // Cleared whenever the result set changes (Search click, Clear, page-size
  // change) — NOT on paging.
  const pageCacheRef = useRef(new Map());
  const clearPageCache = () => { pageCacheRef.current = new Map(); };

  // Fire-and-forget fetch of one page into the page cache.
  const prefetchPage = useCallback((targetPage, cursorArg) => {
    const cache = pageCacheRef.current;
    if (cache.has(targetPage)) return; // loaded or already in flight
    const qs = buildShipmentSearchQs({ filters, page: targetPage, pageSize, cursor: cursorArg, packNumFind });
    const promise = fetch(`${API_URL}/api/shipments?${qs}`, { headers: authHeaders() })
      .then((r) => r.json().then((data) => {
        if (!r.ok || data.success === false) throw new Error("prefetch failed");
        const normalized = normalizeShipmentsResponse(data);
        // Only commit if this cache generation is still current (a new search
        // may have swapped the Map while we were in flight).
        if (pageCacheRef.current === cache) cache.set(targetPage, normalized);
        return normalized;
      }))
      .catch(() => { cache.delete(targetPage); return null; });
    cache.set(targetPage, promise);
  }, [filters, pageSize, packNumFind]);

  // Warm the page after the one just shown, using its boundary cursor (live
  // mode) or the plain page number (known-total mode).
  const prefetchNext = useCallback((current, fromPage) => {
    if (current.hasMore !== undefined) {
      if (current.hasMore === true && current.lastPackNum != null)
        prefetchPage(fromPage + 1, { dir: "next", packNum: current.lastPackNum });
    } else if (fromPage + 1 <= (current.totalPages || 0)) {
      prefetchPage(fromPage + 1, null);
    }
  }, [prefetchPage]);

  // `silent` = background revalidate over a cache-seeded grid: no spinner, the
  // cached rows stay put until the fresh page lands.
  // `reprice` = the Issue 13 re-read: the page on screen still has amounts the
  // API had not priced; skip the page cache (it holds that unpriced copy) and
  // fetch the same page again so the now-priced rows replace it in place.
  const repriceTries = useRef(0);
  const runSearch = useCallback(async ({ silent = false, reprice = false } = {}) => {
    setError(null);
    if (!reprice) repriceTries.current = 0;   // a user-driven search starts the schedule over

    // The mount query — blank filters, page 1 of 25, newest first — is the one
    // the session cache holds; every fresh copy of it is written back so a
    // return visit binds instantly.
    const isDefaultSearch =
      page === 1 && pageSize === 25 && !cursor && !packNumFind.trim() &&
      Object.values(filters).every((v) => !v);

    // Page cache first: a visited page binds instantly; an in-flight prefetch
    // is awaited (spinner for its remaining time) instead of duplicated.
    let entry = reprice ? undefined : pageCacheRef.current.get(page);
    if (entry) {
      if (typeof entry.then === "function") {
        if (!silent) setLoading(true);
        entry = await entry; // normalized results, or null if the prefetch failed
      }
      if (entry) {
        setResults(entry);
        setLoading(false);
        setHasSearched(true);
        if (isDefaultSearch) searchCache.put(SHIPMENTS_KEY, entry);
        prefetchNext(entry, page);
        return;
      }
    }

    if (!silent) setLoading(true);
    const qs = buildShipmentSearchQs({ filters, page, pageSize, cursor, packNumFind });

    try {
      const r = await fetch(`${API_URL}/api/shipments?${qs}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        setError(data.error || data.message || `HTTP ${r.status}`);
        setResults({ shipments: [], totalCount: 0, totalPages: 0 });
      } else {
        const normalized = normalizeShipmentsResponse(data);
        setResults(normalized);
        pageCacheRef.current.set(page, normalized);
        if (isDefaultSearch) searchCache.put(SHIPMENTS_KEY, normalized);
        prefetchNext(normalized, page);
      }
    } catch (e) {
      setError(e.message);
      setResults({ shipments: [], totalCount: 0, totalPages: 0 });
    } finally {
      setLoading(false);
      setHasSearched(true);
    }
  }, [filters, page, pageSize, packNumFind, cursor, prefetchNext]);

  // Auto-load the first page on mount: newest shipments in the default window.
  // When the grid was seeded from the session cache this runs as a silent
  // revalidate — the cached rows stay on screen and refresh in place.
  useEffect(() => {
    runSearch({ silent: !initialFilters && searchCache.peek(SHIPMENTS_KEY) !== undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Issue 13: while the page on screen has amounts the API has not priced yet,
  // re-read it on a short schedule (silent — the rows stay put and update in
  // place). Resets whenever a page arrives fully priced or a new search runs.
  useEffect(() => {
    if (!(results.amountsPending > 0)) { repriceTries.current = 0; return undefined; }
    if (repriceTries.current >= REPRICE_DELAYS_MS.length) return undefined;
    const delay = REPRICE_DELAYS_MS[repriceTries.current];
    const id = window.setTimeout(() => {
      repriceTries.current += 1;
      runSearch({ silent: true, reprice: true });
    }, delay);
    return () => window.clearTimeout(id);
  }, [results, runSearch]);

  // Re-run on page/pageSize change once the first load has happened. Skips the
  // mount render — hasSearched can start true off the cache seed, and the
  // mount effect above already searches.
  const pagingMounted = useRef(false);
  useEffect(() => {
    if (!pagingMounted.current) { pagingMounted.current = true; return; }
    if (hasSearched) runSearch();
  }, [page, pageSize]);  // eslint-disable-line react-hooks/exhaustive-deps

  // One-line recap of what's applied, shown on the collapsed ribbon.
  const activeFilterSummary = (() => {
    const parts = [];
    if (packNumFind.trim()) parts.push(`Pack #${packNumFind.trim()}`);
    if (filters.orderNum.trim()) parts.push(`Order #${filters.orderNum.trim()}`);
    if (filters.custID) parts.push(filters.custID);
    if (filters.trackingNumber) parts.push(`Trk ${filters.trackingNumber}`);
    const st = STATUS_OPTIONS.find((o) => o.value === filters.status);
    if (filters.status && st) parts.push(st.label);
    const inv = INVOICED_OPTIONS.find((o) => o.value === filters.invoiced);
    if (filters.invoiced && inv) parts.push(inv.label);
    return parts.length ? parts.join(" · ") : "";
  })();

  const handleSearchClick = () => {
    setFiltersCollapsed(true);
    // A new search invalidates the cursor — its PackNum bounds belong to the
    // previous result set — and the page cache with it (Search always means
    // "fetch fresh").
    clearPageCache();
    setCursor(null);
    if (page !== 1) setPage(1); else runSearch();
  };

  const handleClearFilters = () => {
    setFilters({ custID: "", status: "", invoiced: "", trackingNumber: "", orderNum: "" });
    setPackNumFind("");
    setColFilters(emptyColFilters);
    clearPageCache();
    setCursor(null);
    setPage(1);
  };

  // Paging. Live mode (hasMore defined) pages by cursor ±1; the cached path
  // keeps numbered jumps. Mirrors Order.js.
  const goToPage = (targetZeroBased) => {
    const target = targetZeroBased + 1;
    if (target === page) return;

    const liveMode = results.hasMore !== undefined;
    if (!liveMode) { setPage(target); return; }

    if (target > page && results.lastPackNum != null) {
      setCursor({ dir: "next", packNum: results.lastPackNum });
      setPage(page + 1);
    } else if (target < page && results.firstPackNum != null) {
      if (page <= 2) {
        setCursor(null);
        setPage(1);
      } else {
        setCursor({ dir: "prev", packNum: results.firstPackNum });
        setPage(page - 1);
      }
    }
  };

  // Client-side per-column filtering of the loaded page.
  const cnorm = (s) => String(s ?? "").toLowerCase();
  const visibleShipments = (() => {
    const f = colFilters;
    if (!Object.values(f).some((v) => v.trim() !== "")) return results.shipments;
    return results.shipments.filter((s) => {
      const st = shipmentStatusText(s);
      return (!f.packNum  || cnorm(s.packNum).includes(cnorm(f.packNum)))
          && (!f.customer || cnorm(`${s.customerName} ${s.customerCustID}`).includes(cnorm(f.customer)))
          && (!f.orderNum || cnorm(s.orderNum).includes(cnorm(f.orderNum)))
          && (!f.shipDate || cnorm(fmtDate(s.shipDate)).includes(cnorm(f.shipDate)))
          && (!f.shipVia  || cnorm(`${s.shipViaCode} ${s.shipViaCodeDescription}`).includes(cnorm(f.shipVia)))
          && (!f.tracking || cnorm(s.trackingNumber).includes(cnorm(f.tracking)))
          && (!f.total    || (s.amountPending
                                ? "pricing".includes(cnorm(f.total))
                                : (cnorm(fmtMoney(s.docOrderAmt)).includes(cnorm(f.total)) || cnorm(s.docOrderAmt).includes(cnorm(f.total)))))
          && (!f.status   || cnorm(st).includes(cnorm(f.status)));
    });
  })();

  const openShipment = (s) => {
    // Read-only shipment view (header, lines, tracking, linked order).
    navigate(`/shipment/${s.packNum}`);
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flow-root" }}>
    <div className="qw-root content-halo dyn-qw dyn-search-shell">
      {/* Ribbon header — truck icon left, centered title. (No page shortcut on
          the right — Orders/Quotes/Shipping are all reachable from the navbar.) */}
      <div className="qw-header qw-header-centered" style={{ marginBottom: 10, padding: "10px 20px" }}>
        <div className="qw-header-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="5" width="14" height="12" rx="1" />
            <path d="M15 9h4l4 4v4h-8" />
            <circle cx="6" cy="19" r="2" />
            <circle cx="18" cy="19" r="2" />
          </svg>
        </div>
        <div className="qw-header-title">Shipping History</div>
      </div>

      {/* ── Filters panel ─────────────────────────────────────────────────── */}
      <div>
      <CollapsibleFilters collapsed={filtersCollapsed} onCollapsedChange={setFiltersCollapsed}
        summary={activeFilterSummary}>
      <div className="dyn-qw-panel" style={{ padding: "10px 14px", marginBottom: 10 }}>
        <div className="dyn-qw-filters dyn-qw-filters-q12 dyn-qw-filters-q14">
          {/* Pack # — exact match (press Enter or Search). */}
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Pack #</label>
            <Form.Control
              size="sm"
              placeholder="Pack #…"
              value={packNumFind}
              onChange={(e) => setPackNumFind(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearchClick(); }}
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Order #</label>
            <Form.Control
              size="sm"
              placeholder="Order #…"
              value={filters.orderNum}
              onChange={(e) => setFilters((f) => ({ ...f, orderNum: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearchClick(); }}
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Customer</label>
            <SearchableSelect
              config={customerConfig}
              value={filters.custID}
              onChange={(v) => setFilters((f) => ({ ...f, custID: v }))}
              onSelectItem={(item) => {
                const id = item.custID || item.CustID || "";
                setFilters((f) => ({ ...f, custID: id }));
              }}
              placeholder="ID or name…"
              inputWidth="100%"
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Tracking #</label>
            <Form.Control
              size="sm"
              placeholder="Tracking…"
              value={filters.trackingNumber}
              onChange={(e) => setFilters((f) => ({ ...f, trackingNumber: e.target.value }))}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearchClick(); }}
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Status</label>
            <Form.Select size="sm" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Invoiced</label>
            <Form.Select size="sm" value={filters.invoiced} onChange={(e) => setFilters((f) => ({ ...f, invoiced: e.target.value }))}>
              {INVOICED_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </div>
          <div className="dyn-qw-filter-group dyn-qw-actions-cell">
            <div className="d-flex gap-2" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
              <Button type="button" variant="outline-secondary" size="sm" onClick={handleClearFilters} disabled={loading}>
                Clear
              </Button>
              <Button type="button" variant="primary" size="sm" onClick={handleSearchClick} disabled={loading} style={{ fontWeight: 600 }}>
                {loading
                  ? <><Spinner animation="border" size="sm" /> Searching…</>
                  : <><FaSearch style={{ marginRight: 6 }} /> Search</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
      </CollapsibleFilters>
      </div>

      {/* ── Results panel — scrolls internally so the page stays put ───────── */}
      <div className="dyn-results-wrap">
      <div ref={resultsScrollRef} className="dyn-results-scroll" onScroll={onResultsScroll}>
      <div className="dyn-qw-panel" style={{ padding: "10px 14px" }}>
          {error && <Alert variant="danger">{error}</Alert>}

              {/* Desktop grid (≥992px) */}
              <div className="d-none d-lg-block" style={{ border: "1px solid #E2E8F0", borderRadius: 12, background: "#fff" }}>
              <table className="dyn-table-dense" style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
                <colgroup>
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "9%" }} />
                  <col style={{ width: "11%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "12%" }} />
                </colgroup>
                <thead>
                  <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#64748B", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
                    <th style={QTH}>Pack #</th>
                    <th style={QTH}>Customer</th>
                    <th style={QTH}>Order #</th>
                    <th style={QTH}>Ship Date</th>
                    <th style={QTH}>Ship Via</th>
                    <th style={QTH}>Tracking</th>
                    <th style={QTH}>Total</th>
                    <th style={QTH}>Status</th>
                  </tr>
                  {showColFilters && (
                  <tr className="dyn-colfilter">
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.packNum} onChange={(e) => setCol("packNum", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.customer} onChange={(e) => setCol("customer", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.orderNum} onChange={(e) => setCol("orderNum", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.shipDate} onChange={(e) => setCol("shipDate", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.shipVia} onChange={(e) => setCol("shipVia", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.tracking} onChange={(e) => setCol("tracking", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.total} onChange={(e) => setCol("total", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.status} onChange={(e) => setCol("status", e.target.value)} /></th>
                  </tr>
                  )}
                </thead>
                <tbody>
                  {visibleShipments.map((s) => (
                    <tr
                      key={`${s.shipmentSource}-${s.packNum}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => openShipment(s)}
                      title="Click to view shipment details"
                    >
                      <td className="fw-semibold" style={{ color: "#0052A5" }}>#{s.packNum}</td>
                      <td>
                        <div>{s.customerName || "—"}</div>
                        {s.customerCustID && <div className="dyn-cell-sub">{s.customerCustID}</div>}
                      </td>
                      <td>{s.orderNum > 0 ? s.orderNum : "—"}</td>
                      <td>{fmtDate(s.shipDate)}</td>
                      <td>
                        <div>{s.shipViaCode || "—"}</div>
                        {s.shipViaCodeDescription && <div className="dyn-cell-sub">{s.shipViaCodeDescription}</div>}
                      </td>
                      <td>
                        <TrackingCell s={s} />
                      </td>
                      <td><AmountCell s={s} /></td>
                      <td>
                        <div className="dyn-cell-inline">
                          <ShipmentStatusBadge s={s} />
                          <InvoicingText s={s} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loading && (
                <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
                  <Spinner animation="border" size="sm" /> Loading shipments…
                </div>
              )}
              {!loading && results.shipments.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>
                  {hasSearched ? "No shipments match the current filters." : "Set filters and hit Search."}
                </div>
              )}
              {!loading && results.shipments.length > 0 && visibleShipments.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>
                  No rows on this page match the column filters.
                </div>
              )}
              </div>

              {/* Mobile cards (<992px) — one card per shipment, tap to view shipment */}
              <div className="d-lg-none">
                {!filtersCollapsed ? (
                  <Alert variant="light" className="text-center mb-0" style={{ fontSize: 13 }}>
                    Set filters above, then tap <strong>Search</strong> to see results.
                  </Alert>
                ) : (<>
                {loading && (
                  <div style={{ padding: 30, textAlign: "center", color: "#666" }}>
                    <Spinner animation="border" size="sm" /> Loading shipments…
                  </div>
                )}
                {!loading && hasSearched && results.shipments.length === 0 && !error && (
                  <Alert variant="light">No shipments match the current filters.</Alert>
                )}
                {!loading && results.shipments.map((s) => (
                  <div
                    key={`${s.shipmentSource}-${s.packNum}`}
                    onClick={() => openShipment(s)}
                    className="qw-quote-card"
                  >
                    {/* Row 1: pack # · customer (+ id) · status */}
                    <div className="qc-row">
                      <span className="qc-num">#{s.packNum}</span>
                      <span className="qc-cust" title={s.customerName || ""}>
                        {s.customerName || "—"}
                        {s.customerCustID ? <span className="qc-custid"> · {s.customerCustID}</span> : null}
                      </span>
                      <ShipmentStatusBadge s={s} />
                    </div>
                    {/* Row 2: total · order/ship-via · date */}
                    <div className="qc-row">
                      <span className="qc-total"><AmountCell s={s} /></span>
                      <span className="qc-meta">
                        {s.orderNum > 0 ? `Order ${s.orderNum}` : "—"}
                        {s.shipViaCode ? ` · ${s.shipViaCode}` : ""}
                      </span>
                      <span className="qc-dates">{fmtDateShort(s.shipDate)}</span>
                    </div>
                    {/* Row 3: tracking + invoicing, when present. */}
                    {(s.trackingNumber || s.invoiced || s.readyToInvoice) && (
                      <div className="qc-row" style={{ gap: 8 }}>
                        <TrackingCell s={s} compact />
                        <InvoicingText s={s} />
                      </div>
                    )}
                  </div>
                ))}
                </>)}
              </div>

      </div>
      </div>
      {moreBelow && <div className="dyn-results-fade" aria-hidden="true" />}
      </div>
      {/* Pinned pagination footer (outside the scroll region). */}
      {hasSearched && (
        <GridPagination
          currentPage={page - 1}
          // Always the simple Prev/Next pager (hasNext defined ⇒ no numbered
          // pages). Live mode: the server states whether more rows exist.
          // Cached mode has a real total — compare against the page count so
          // Next stays alive past page 1 (hasMore is undefined there).
          hasNext={results.hasMore !== undefined
            ? results.hasMore === true
            : page < (results.totalPages || 0)}
          totalPages={results.totalPages || 0}
          totalCount={results.hasMore !== undefined
            ? null
            : (results.totalCount ?? results.shipments.length)}
          showFrom={results.shipments.length ? (page - 1) * pageSize + 1 : 0}
          showTo={(page - 1) * pageSize + results.shipments.length}
          onGoToPage={goToPage}
          extra={
            <div className="d-flex align-items-center" style={{ gap: 10 }}>
              <button
                type="button"
                onClick={() => setShowColFilters((v) => {
                  if (v) setColFilters(emptyColFilters);
                  return !v;
                })}
                title={showColFilters
                  ? "Hide the in-grid column search (frees a row of height)"
                  : "Show the in-grid column search — filters the rows on this page"}
                style={{
                  border: "1px solid #E2E8F0", background: showColFilters ? "#EDE9FE" : "#fff",
                  color: showColFilters ? "#5B21B6" : "#64748B", borderRadius: 8,
                  fontSize: 12, fontWeight: 600, padding: "4px 10px", cursor: "pointer",
                }}
              >
                <FaSearch style={{ marginRight: 5, fontSize: 10 }} />
                Column filters
              </button>
              <span style={{ fontSize: 12, color: "#64748B" }}>Rows</span>
              <Form.Select size="sm" value={pageSize} onChange={(e) => { clearPageCache(); setCursor(null); setPage(1); setPageSize(Number(e.target.value)); }} style={{ width: "auto", minWidth: 64 }} aria-label="Page size">
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            </div>
          }
        />
      )}
    </div>
    </div>
  );
}

// =============================================================================
// Status helpers — priority mirrors the shipment lifecycle:
// Voided > Shipped > Staged > Open (anything else, e.g. "OPEN").
// =============================================================================
// Observed Epicor ShipStatus values: SHIPPED, INVOICED, CLOSED, OPEN, STAGED.
function shipmentStatusText(s) {
  if (s.voided) return "Voided";
  const st = (s.shipStatus || "").toLowerCase();
  if (st.includes("invoic")) return "Invoiced";
  if (st.includes("ship"))   return "Shipped";
  if (st.includes("stage"))  return "Staged";
  if (st.includes("close"))  return "Closed";
  if (st.includes("open") || !s.shipStatus) return "Open";
  return s.shipStatus;
}

function ShipmentStatusBadge({ s }) {
  const text = shipmentStatusText(s);
  const bg =
    text === "Voided"   ? "danger"
    : text === "Invoiced" || text === "Shipped" ? "success"
    : text === "Staged"  ? "warning"
    : text === "Closed"  ? "secondary"
    : "primary";
  return <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>{text}</Badge>;
}

// Secondary invoicing note next to the badge. Skipped when the badge itself
// already reads "Invoiced" (ShipStatus=INVOICED) — no point saying it twice.
function InvoicingText({ s }) {
  const badge = shipmentStatusText(s);
  const text =
    s.invoiced && badge !== "Invoiced" ? "Invoiced"
    : !s.invoiced && s.readyToInvoice  ? "Ready to invoice"
    : null;
  if (!text) return null;
  return <span className="dyn-cell-sub" style={{ marginTop: 0 }}>{text}</span>;
}

// Tracking number — a FedEx deep link when the carrier looks like FedEx (the
// stopPropagation keeps the row's order-view click from also firing), plain
// monospace text otherwise.
function TrackingCell({ s, compact }) {
  if (!s.trackingNumber) return compact ? null : <span>—</span>;
  const style = { fontFamily: "ui-monospace, monospace", fontSize: compact ? 12 : 12.5, wordBreak: "break-all" };
  if (isFedEx(s)) {
    return (
      <a
        href={`https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(s.trackingNumber)}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        style={{ ...style, color: "#4D148C", textDecoration: "underline" }}
        title="Track on fedex.com"
      >
        {s.trackingNumber}
      </a>
    );
  }
  return <span style={style}>{s.trackingNumber}</span>;
}

export default Shipment;
